import { Company, ProfileSingle } from '@assets/svg';
import { Text, Steps, ChoiceInput } from '@components';
import { Box, useTheme } from '@mui/material';
import InvestmentFooter from '@pages/Investment/InvestmentFooter/InvestmentFooter';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentContainer } from './InvestmentProfile.styles';
import {
  InvestmentProfileFormData,
  InvestmentProfileType,
} from './InvestmentProfile.types';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { TOTAL_STEPS } from '../Investment.consts';

const InvestmentProfile: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();

  const { productId } = useParams();

  const [data, setData] = useState<InvestmentProfileFormData>();

  const onSubmit = async () => {
    if (!data?.type) return;

    if (data.type === 'individual') {
      navigate(`/investment-subscription/${productId}/progress`, {
        state: {
          progress: 2,
          type: 'individual',
        },
        replace: true,
      });
    } else if (data.type === 'legal_entity') {
      navigate(`/investment-subscription/${productId}/progress`, {
        state: {
          progress: 2,
          type: data.type,
        },
        replace: true,
      });
    } else {
      navigate(`/investment-subscription/${productId}/unavailable`, {
        state: {
          progress: 1,
          type: data.type,
          previousPathName: 'profile',
        },
        replace: true,
      });
    }
  };

  useEffect(() => {
    if (user.personalDetailsLocked) {
      getPersonalDetailLockNavigation(navigate, productId)();
    }
  }, [user, navigate, productId]);

  return (
    <InvestmentContainer data-testid='investment-profile-container'>
      <ContentContainer>
        <Steps currentStep={2} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          mt={5}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.profile.investmentProfile' />
        </Text>
        <Text
          variant='h6'
          mt={5}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          <FormattedMessage id='investment.profile.message' />
        </Text>
        <Box mt={12}>
          <ChoiceInput
            direction='column'
            fullWidth
            choices={[
              {
                id: 'individual',
                title: `${user.basicDetails.firstName} ${user.basicDetails.lastName}`,
                adornmentLeft: <ProfileSingle />,
              },
              {
                id: 'legal_entity',
                title: intl.formatMessage({
                  id: 'investment.profile.legalEntity',
                }),
                adornmentLeft: <Company />,
              },
            ]}
            onChange={(current) =>
              setData({
                ...data,
                type: current as InvestmentProfileType,
              })
            }
          />
        </Box>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/amount`, {
              state: {
                progress: 0,
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !data?.type,
          onClick: onSubmit,
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(InvestmentProfile);
